<template>
  <div>
    <Layout
      v-if="invalidInvitation && app"
      :app="app"
      title="This inivitation has already been used!"
    >
      <div class="links">
        <a
          class="link"
          :href="app && app.url"
        >
          Click here to continue
        </a>
      </div>
    </Layout>
    <Layout
      v-else
      :app="hasInvitationData && app"
      :title="title"
      header="Account creation"
    >
      <template
        v-if="!userExists"
        v-slot:desc
      >
        Fill in your password and name (optional)
      </template>

      <ul
        v-if="errors"
        class="error"
      >
        <li
          v-for="err in errors"
          :key="err.message"
        >
          {{ err.message }}
        </li>
      </ul>

      <div
        v-if="completed"
        class="completed"
      >
        Account {{ userExists ? 'linked' : 'created' }}. Redirecting...
      </div>

      <Input
        v-if="!userExists && userInvite"
        v-model="userInvite.name"
        class="input"
        label="Name(optional)"
        type="text"
        placeholder="Donald Draper"
        @enter="submit"
      />

      <Input
        v-if="!userExists"
        v-model="password"
        class="input"
        label="Password(*)"
        type="password"
        placeholder="******"
        @enter="submit"
      />

      <Input
        v-if="!userExists"
        v-model="password2"
        class="input"
        label="Retype Password(*)"
        type="password"
        placeholder="******"
        @enter="submit"
      />

      <div
        v-if="loading"
        class="loading"
      >
        <Spinner />
      </div>
      <Button
        v-else
        class="button"
        :label="submitButtonLabel"
        :class="{'disabled': submitButtonDisabled}"
        @click="submit"
      />

      <div
        v-if="app && !isAdminApp"
        class="links"
      >
        <a
          class="link"
          :href="app && app.url"
        >Back to {{ app.name }}</a>
        <router-link
          class="link"
          :to="`/${appKey}/sign-in`"
        >
          Sign In
        </router-link>
      </div>
    </Layout>
  </div>
</template>

<script>
  import gql from 'graphql-tag';
  import { ADMIN_APP_KEY } from '@/globals';
  import Layout from './layout.vue';
  import Input from '../../components/input.vue';
  import Button from '../../components/button.vue';
  import Spinner from '../../components/spinner.vue';
  import app from '../../mixins/app';

  export default {
    name: 'AccountCreation',
    components: {
      Layout,
      Input,
      Button,
      Spinner,
    },
    mixins: [app],
    data() {
      return {
        errors: null,
        loading: false,
        completed: false,
        password: '',
        password2: '',
        userInvite: null,
        userExits: null,
        invitationDoesNotExist: null,
      };
    },
    apollo: {
      userExists: {
        query: gql`query ($email: String!) {
          userExists (email: $email)
        }`,
        skip() {
          return !this.userInvite;
        },
        variables() {
          return {
            email: this.userInvite.email,
          };
        },
      },
      userInvite: {
        query: gql`
          query ($id: String!) {
            userInvite(where: {id: $id}) {
              id
              email
              name
            }
          }
        `,
        variables() {
          return {
            id: this.$route.params.id,
          };
        },
        result({ data }) {
          this.invitationDoesNotExist = !data.userInvite;
        },
      },
    },
    computed: {
      submitButtonDisabled() {
        return !this.userExists && (this.password === '' || this.password2 === '' || this.password !== this.password2);
      },
      hasInvitationData() {
        return this.userInvite && typeof this.userExists === 'boolean';
      },
      invalidInvitation() {
        return this.invitationDoesNotExist === true;
      },
      submitButtonLabel() {
        return this.userExists ? 'Link account' : 'Create account';
      },
      title() {
        return this.userExists ? 'Link your account' : 'Create your account';
      },
      isAdminApp() {
        return this.appKey === ADMIN_APP_KEY;
      },
    },
    watch: {
      password2(password2) {
        if (password2 !== this.password) {
          this.errors = [{ message: 'Password fields have to be identical' }];
        } else {
          this.errors = null;
        }
      },
    },
    methods: {
      submit() {
        this.errors = null;
        this.loading = true;
        this.$apollo.mutate({
          mutation: gql`mutation($id: String!, $email: String!, $name: String, $password: String) {
              createUser(id: $id, email: $email, name: $name, password: $password)
            }`,
          variables: {
            id: this.$route.params.id,
            email: this.userInvite.email,
            name: this.userInvite.name,
            password: this.password,
          },
        }).then(() => {
          this.loading = false;
          this.completed = true;
          setTimeout(() => {
            window.location.href = this.app.redirect;
          }, 1000);
        }).catch((error) => {
          this.loading = false;
          this.errors = error.graphQLErrors;
        });
      },
    },
  };
</script>

<style scoped lang="stylus">
  @require '~@/style/colors'
  @require '~@/style/type'

  .input
    margin-top: 30px
    &:first-of-type
      margin-top: 0

  .button, .loading
    width: 100%
    margin-top: 60px

  .button.disabled
    opacity: 0.5
    pointer-events: none

  .loading
    height: 50px
    display: flex
    align-items: center
    justify-content: center

  .links
    @extend $type-small
    display: block
    width: 100%
    text-align: center
    margin-top: 30px
    a
      display: inline-block
      margin-left: 10px
      padding-right: 10px
      border-right: 1px solid #aaa
      &:last-of-type
        border-right: none

  .error
    @extend $type-small, $type-medium
    color: #a00
    background-color: rgba(205,92,92,.1)
    text-align: center
    list-style: none
    margin: 0 0 20px 0
    padding: 10px

  .completed
    @extend $type-small, $type-medium
    color: #060
    background-color: rgba(92,205,92,.1)
    text-align: center
    list-style: none
    margin: 0 0 20px 0
    padding: 10px

</style>
